"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import { UserAlreadyExistsError } from "@nelo/signup-lib/dist/utils/Errors"
import React, { useEffect, useState } from "react"

import { CURP_ENTRY_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"

import AppDownloadStep from "./AppDownloadStep"
import InfoBox from "../../common/InfoBox"
import StepWrapper from "../../common/StepWrapper"
import TextInput from "../../common/TextInput"

const CurpEntryStep: React.FC<StepComponentProps> = ({ onComplete, onBack, onClose, progressPercentage, treatments, stepId }) => {
  const curpText = CURP_ENTRY_STEP_TEXTS

  const { curp, generateCurp, validateCurp } = useSignup()
  const [curpValue, setCurpValue] = useState("")
  const [showAppDownload, setShowAppDownload] = useState(false)

  useEffect(() => {
    if (generateCurp.data?.generatedCurp) {
      setCurpValue(generateCurp.data.generatedCurp)
      curp.setValue(generateCurp.data.generatedCurp)
    } else if (curp.value) {
      setCurpValue(curp.value)
    }
  }, [generateCurp.data?.generatedCurp, curp])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    curp.setTouched(true)
    curp.setBlurred(true)
    if (!curp.isValid) {
      return
    }

    try {
      await validateCurp.execute(curpValue)
      onComplete()
    } catch (error) {
      console.error("Error validating CURP:", error)

      if (error instanceof UserAlreadyExistsError) {
        datadogRum.addError(error, {
          step: stepId,
          action: "validateCurp",
          errorType: "UserAlreadyExists",
        })
        setShowAppDownload(true)
      } else {
        datadogRum.addError(error, {
          step: stepId,
          action: "validateCurp",
        })
      }
    }
  }

  const handleCurpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase()
    setCurpValue(value)
    curp.setValue(value)
  }

  if (showAppDownload) {
    return <AppDownloadStep onClose={onClose} />
  }

  return (
    <StepWrapper
      title={curpText.title}
      subtitle={curpText.subtitle}
      progressPercentage={progressPercentage}
      isLoading={validateCurp.isLoading}
      formError={validateCurp.error?.message}
      onSubmit={handleSubmit}
      submitDisabled={!curp.isValid || validateCurp.isLoading}
      showBackButton={true}
      onBack={onBack}
      footerText={
        <>
          {curpText.helpText}{" "}
          <a href="https://www.gob.mx/curp/" target="_blank" rel="noopener noreferrer" className="text-nelo-blue hover:underline">
            , o haciendo clic aquí.
          </a>
        </>
      }
    >
      <div>
        {generateCurp.data?.generatedCurp && (
          <InfoBox>
            <p>{curpText.infoText}</p>
          </InfoBox>
        )}

        <TextInput
          label={curpText.label}
          value={curpValue}
          onChange={handleCurpChange}
          onBlur={() => {
            curp.setTouched(true)
            curp.setBlurred(true)
            if (curp.errorMessage) {
              datadogRum.addError(new Error(`CURP validation error: ${curp.errorMessage}`), {
                step: stepId,
                field: "curp",
              })
            }
          }}
          onFocus={() => curp.setTouched(true)}
          isValid={curp.isValid}
          touched={curp.touched}
          blurred={curp.blurred}
          errorMessage={curp.errorMessage}
          className="uppercase"
          maxLength={18}
        />
      </div>
    </StepWrapper>
  )
}

export default CurpEntryStep
